import { isAxiosError } from 'axios';
import { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import type { DownloadedFile, DownloadProgress } from './api';

/**
 * Hand a fetched file to the browser as a download.
 *
 * The object URL is revoked on the next tick rather than straight after the
 * click: some browsers start reading the blob asynchronously, and revoking it
 * synchronously can cancel the download before it begins.
 */
export function saveFile({ blob, filename }: DownloadedFile): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * What to tell the user when a download fails, in the backend's own words.
 *
 * The request asks for a blob, so an error body arrives as a Blob too. The
 * refusals worth reading (expired rights, not a source image, file missing)
 * are JSON `{"detail": {"code", "message"}}`; anything else gets a generic
 * line rather than a status code.
 */
export async function downloadErrorMessage(error: unknown): Promise<string> {
  if (!isAxiosError(error) || !error.response) {
    return 'Download failed. Check your connection and try again.';
  }
  const data: unknown = error.response.data;
  try {
    const body = JSON.parse(data instanceof Blob ? await data.text() : String(data)) as {
      detail?: { message?: string } | string;
    };
    const detail = body.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
  } catch {
    // Not JSON: a proxy error page, say. Fall through to the generic line.
  }
  return 'Download failed. Please try again.';
}

/** Where one download stands. */
export interface DownloadStatus {
  isDownloading: boolean;
  /** Fraction done, or null when unknown (a streamed zip has no Content-Length). */
  progress: number | null;
}

const IDLE: DownloadStatus = { isDownloading: false, progress: null };

/**
 * Downloads tracked by key, each with its own progress.
 *
 * Keyed because the component that owns this outlives any one asset: the
 * detail overlay steps between assets with the arrow keys, and a single
 * "is downloading" flag would follow the user onto the next asset, showing
 * that one as busy while the first one's file was still on its way. The key
 * is whatever names the download: an asset id, or the selection for a zip.
 *
 * One download per key at a time, guarded by a ref rather than the state:
 * state updates land after the render, so two quick clicks would both see
 * an idle key and start the same download twice, which is exactly what a
 * silent button invites. Different keys may run side by side.
 */
export function useFileDownload(): {
  download: (
    key: string,
    fetchFile: (onProgress: DownloadProgress) => Promise<DownloadedFile>,
  ) => Promise<void>;
  statusOf: (key: string) => DownloadStatus;
} {
  const inFlight = useRef(new Set<string>());
  const [running, setRunning] = useState<ReadonlyMap<string, number | null>>(new Map());

  const download = useCallback(
    async (key: string, fetchFile: (onProgress: DownloadProgress) => Promise<DownloadedFile>) => {
      if (inFlight.current.has(key)) return;
      inFlight.current.add(key);
      setRunning((current) => new Map(current).set(key, null));
      // Ignores progress that arrives after the key was cleared, so a late
      // event cannot resurrect a finished download.
      const onProgress: DownloadProgress = (fraction) =>
        setRunning((current) => (current.has(key) ? new Map(current).set(key, fraction) : current));
      try {
        saveFile(await fetchFile(onProgress));
      } catch (error) {
        toast.error(await downloadErrorMessage(error));
      } finally {
        inFlight.current.delete(key);
        setRunning((current) => {
          const next = new Map(current);
          next.delete(key);
          return next;
        });
      }
    },
    [],
  );

  const statusOf = useCallback(
    (key: string): DownloadStatus =>
      running.has(key) ? { isDownloading: true, progress: running.get(key) ?? null } : IDLE,
    [running],
  );

  return { download, statusOf };
}
